import { fetchWithTransientRetry } from "@/lib/transient-fetch";
import { searchRequestApiPath } from "@/lib/search-request-params";
import { listingDistanceUnit } from "@workspace/countries";
import type { SearchResultMeta } from "@/lib/search-location-intent";
import { countryCodeToMarketLocale } from "@workspace/countries";
import {readResolvedCarIdentity, rememberCanonicalPayload} from "../../../../lib/copy/src/public-canonical-client.mjs";
import {isDriveshareLegacySeoEnabled} from "../../../../lib/copy/src/legacy-seo-switch.mjs";

const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

export class ApiError extends Error {
  status: number;
  code: string | null;
  body: any;
  constructor(message: string, status: number, body: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.code = typeof body?.code === "string" ? body.code : null;
  }
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  // Task #5799 — retry idempotent (GET) reads such as car availability /
  // reviews on a transient 5xx / network blip before surfacing an error.
  // Writes (POST newsletter subscribe/unsubscribe) carry a method and are
  // never retried by the helper.
  const res = await fetchWithTransientRetry(`${API_BASE}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...init?.headers },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(body.error || `API error ${res.status}`, res.status, body);
  }
  const data = await res.json();
  rememberCanonicalPayload(data);
  return data;
}

export interface CarCardData {
  sourceId?: number | null;
  listingStatus?: string | null;
  deletedAt?: string | null;
  deleted_by_owner?: boolean | null;
  disabled_by_owner?: boolean | null;
  id: number;
  slug: string;
  platformOrigin?: string | null;
  platform_origin?: string | null;
  /** Exact historical US slug when the API has proven this identity. */
  legacySlug?: string | null;
  make: string;
  model: string;
  year: number;
  city: string;
  state: string;
  priceDriving: number | null;
  priceEvent: number | null;
  chauffeurMinPrice: number | null;
  eventMinPrice: number | null;
  eventHourlyRate: number | null;
  chauffeuredHourlyRate: number | null;
  minimumPrice: number | null;
  primaryImageUrl: string | null;
  primaryImageCdnThumbnail?: string | null;
  primaryImageCdnMedium?: string | null;
  primaryImageCdnFull?: string | null;
  // Full image set for the mobile in-card carousel (primary first). Optional:
  // some card sources (e.g. the offline search fallback) only carry a primary
  // image, in which case the card renders a single-image (non-swipe) view.
  images?: Array<{
    url: string | null;
    cdnThumbnail?: string | null;
    width?: number | null;
    height?: number | null;
    cdnMedium?: string | null;
    cdnFull?: string | null;
  }>;
  rentalDrivingActive: boolean;
  rentalChauffeurActive: boolean;
  rentalEventActive: boolean;
  totalScore: number | null;
  /** Curated, admin-set `cars.featured` flag (distinct from the score-driven standout badge). */
  featured?: boolean;
  reviewsRating: number | null;
  reviewsCount: number | null;
  transmission: string | null;
  passengers: number | null;
  currencyCode: string;
  latitude: number | null;
  longitude: number | null;
  countryCode?: string | null;
  ownerProTier?: string | null;
  instantBook?: boolean;
}

export interface PlatformStats {
  carCount: number;
  cityCount: number;
  reviewCount: number;
}

export interface CarType {
  id: number;
  name: string;
  slug: string;
  count?: number;
}

export interface MakeInventoryCount {
  make: string;
  count: number;
}

export interface SearchParams {
  q?: string;
  rental_type?: string;
  location?: string;
  /** Explicitly chosen national suggestion, independent of the location text. */
  location_country?: string;
  country_code?: string;
  price_min?: number;
  price_max?: number;
  /**
   * Which underlying column the price slider is filtering on. Defaults to the
   * rental-type-agnostic waterfall (`filter_price_per_day`) when omitted so
   * chauffeur-only listings (no `price_driving`) are still reachable.
   * See task #1537.
   */
  price_attribute?:
    | "filter_price_per_day"
    | "price_driving"
    | "price_event"
    | "chauffeured_min_price_hourly_rate";
  make?: string;
  /** Case-insensitive substring match on `cars.model` (task #6522). */
  model?: string;
  classification?: string;
  car_type?: string[];
  era?: string[];
  /** OR-selected car colour IDs. */
  colour_id?: string[];
  passengers?: number;
  sort?: string;
  page?: number;
  pageSize?: number;
  date_from?: string;
  date_to?: string;
  features?: string[];
  bounds_north?: number;
  bounds_south?: number;
  bounds_east?: number;
  bounds_west?: number;
  /**
   * Search radius in miles, applied around `anchor_lat`/`anchor_lng` (if
   * supplied) or around the server-side geocode of `location`. Mirrors the
   * Algolia path's typed-location radius pills so the Supabase fallback
   * actually returns nearby cars instead of empty-stating on a strict
   * city/state ILIKE.
   */
  radius?: number;
  anchor_lat?: number;
  anchor_lng?: number;
}

export interface SearchResult {
  cars: CarCardData[];
  total: number;
  page: number;
  pageSize: number;
  /** Client-side context for an automatic nearby recovery; absent for legacy callers. */
  searchMeta?: SearchResultMeta;
}

export interface ReviewData {
  id: string;
  rating: number;
  reviewText: string;
  createdAt: string;
  reviewerName: string;
  reviewerId: string | null;
  publicCollectionPath?: string | null;
  /** Legacy single-category column — kept for backward compat. */
  ratingCategory?: string | null;
  /** Per-category 1-5 ratings (empty for legacy reviews). */
  categoryRatings: { category: string; rating: number }[];
  /** Curated highlight chips chosen by the reviewer. */
  tags: string[];
  /** Renter-attached review photos (moderated at upload). */
  images?: string[];
}

/**
 * Server-computed review summary: only present when at least 3 new-format
 * reviews exist. Drives the overall distribution histogram, per-category
 * bars, and top tag chips.
 */
export interface ReviewSummary {
  eligibleCount: number;
  categoryAverages: Record<string, { avg: number; count: number }>;
  /** Histogram of overall stars: keys "1".."5" matching the wire JSON. */
  ratingDistribution: Record<"1" | "2" | "3" | "4" | "5", number>;
  topTags: { tag: string; count: number }[];
}

export interface CarData {
  /** Exact user-approved public archive; never bookable. */
  public_unavailable?: boolean;
  /** Safe archive preference; general source metadata is never projected. */
  hide_apex?: boolean;
  id: number;
  slug: string;
  platform_origin?: string | null;
  /** Stable legacy public slug for US listings; unlike `slug`, this is never renamed. */
  legacySlug?: string | null;
  /** Snake-case form returned by older detail payloads. */
  legacy_slug?: string | null;
  make: string;
  model: string;
  year: number;
  submodel: string | null;
  city: string;
  state: string;
  country_code: string;
  description: string | null;
  car_history: string | null;
  history?: string | null;
  price_driving: number | null;
  chauffeured_hourly_rate: number | null;
  event_hourly_rate: number | null;
  rental_driving_active: boolean;
  rental_chauffeured_active: boolean;
  rental_event_active: boolean;
  reviews_rating: number | null;
  reviews_count: number | null;
  featured: boolean;
  instant_rental: boolean;
  /**
   * Instant Book scope set by the host: "all" dates, "selected" dates only,
   * or "off". Shipped raw by the detail endpoint (SELECT * spread); legacy
   * rows may carry other historical strings, so only compare against
   * "selected" for the date-aware widget messaging (Task #6387). The
   * authoritative per-date decision is always the server quote's
   * `instantBooking` field — never re-derive it client-side.
   */
  instant_book_mode?: string | null;
  currency_code: string;
  body: string | null;
  category: string | null;
  engine: string | null;
  transmission: string | null;
  color: string | null;
  mileage: number | null;
  mileage_is_accurate: boolean;
  passengers: number | null;
  fuel_type: string | null;
  drive_side: string | null;
  convertible: boolean | null;
  is_convertible: boolean | null;
  air_conditioning: boolean | null;
  has_aircon: boolean | null;
  is_left_hand_drive: boolean | null;
  all_seatbelts: number | null;
  fuel_policy: string | null;
  mileage_limit: boolean | null;
  mileage_limit_miles: number | null;
  pets_allowed: boolean | null;
  delivery_enabled: boolean;
  /** Self-drive-specific delivery opt-in; absent on legacy API responses. */
  self_drive_delivery_enabled?: boolean | null;
  delivery_fee_per_unit: number | null;
  delivery_fee_min: number | null;
  delivery_free: boolean | null;
  delivery_maximum_distance: number | null;
  delivery_radius_free: number | null;
  delivery_radius_full: number | null;
  owner_id: string;
  sort_order: number;
  origin_country: string | null;
  max_speed: number | null;
  wheels_and_tires: string | null;
  brakes: string | null;
  classification: string | null;
  decade: number | null;
  nonsmoking: boolean | null;
  allow_decorations: boolean | null;
  date_listed: string | null;
  latitude: number | null;
  longitude: number | null;
  meta: Record<string, unknown> | null;
  price_event: number | null;
  chauffeured_min_price_hourly_rate: number | null;
  event_min_price_hourly_rate: number | null;
  minimum_price: number | null;
  insured_value: number | null;
  /**
   * Task #6590 — self-drive refundable security deposit (`cars.deposit`).
   * Already on the wire (the detail endpoint SELECTs *); surfaced above the
   * fold on the car detail page. Null/0 = no deposit hold.
   */
  deposit?: number | null;
  feature_conditioning: boolean | null;
  feature_heat: boolean | null;
  feature_radio: boolean | null;
  feature_aux: boolean | null;
  feature_steering: boolean | null;
  feature_modified: boolean | null;
  condition: number | null;
}

export interface StandoutScoreData {
  car_id: number;
  total_score: number;
  significance_and_story: number | null;
  design_and_presence: number | null;
  engineering_and_driving: number | null;
  build_durability_risk: number | null;
  rarity_and_spec_gravity: number | null;
  cultural_relevance: number | null;
  market_momentum: number | null;
  one_liner: string | null;
  listing_blurb: string | null;
  tags: string[] | null;
  justification_significance: string | null;
  justification_design: string | null;
  justification_engineering: string | null;
  justification_build: string | null;
  justification_rarity: string | null;
  justification_cultural: string | null;
  justification_market: string | null;
  collector_tier: string | null;
  era_style: string | null;
  audience_appeal: string[] | null;
  lifestyle_categories: string[] | null;
  famous_movie_appearances: Record<string, unknown> | string[] | null;
}

export interface DeliveryAirport {
  airport_code: string;
  airport_name: string;
  flat_fee: number;
}

export interface OwnerResponseStats {
  responseRate: number;
  avgResponseMinutes: number | null;
}

// Task #6806 — at most one real-data scarcity signal per car, resolved
// server-side (priority rarity > demand > availability) behind the
// `feature_scarcity_banner` flag. Null/absent = no banner.
export interface ScarcitySignal {
  type: "rarity" | "demand" | "availability";
  recentTripCount?: number;
  favoritesCount?: number;
  blockedDays?: number;
  windowDays?: number;
}

export interface CarDetail {
  car: CarData;
  /** Public, server-confirmed entitlement; independent of the viewer's account access. */
  ownerProTier?: string | null;
  images: Array<{ id: string; url: string; r2_path: string | null; display_order: number; cdn_thumbnail?: string | null; cdn_medium?: string | null; cdn_large?: string | null; cdn_full?: string | null; intrinsic_dimensions?: Record<string, { width: number; height: number }> }>;
  unresolved_image_dimensions?: { url: string; reason: string }[];
  standoutScore: StandoutScoreData | null;
  movieAppearances?: string[];
  reviewSummary: ReviewSummary | null;
  reviews: ReviewData[];
  deliveryAirports: DeliveryAirport[];
  owner: { id: string; publicCollectionPath?: string | null; first_name: string; last_name: string; about: string; created_at: string; city: string; state: string; profile_image: string | null; profile_image_dimensions?: { width: number; height: number } | null; responseStats: OwnerResponseStats | null } | null;
  carTypes: CarType[];
  eventTypes: CarType[];
  similarCars: CarCardData[];
  // Task: video listings v1. Server returns only `ready` rows so the
  // detail page can iframe-embed them directly.
  videos?: Array<{
    id: number;
    stream_uid: string;
    status: string;
    duration_seconds: number | null;
    thumbnail_url: string | null;
    playback_url: string | null;
    display_order: number;
  }>;
  scarcity?: ScarcitySignal | null;
}

export interface InsurancePackage {
  id: number;
  name: string;
  owner_liability_coverage: string;
  renter_liability_coverage: string;
  physical: string;
  liability: string;
}

export interface AvailabilityData {
  blockedDates: Array<{ start_date: string; end_date: string; reason: string }>;
  /** Time-specific conflicts for chauffeured/event bookings. Values are epoch
   * milliseconds and include the required preparation/turnaround buffers. */
  timedIntervals?: Array<{ start: number; end: number; reason: "booking" | string }>;
  window: { min_booking_days: number; advance_notice_days: number; available_from: string | null; available_to: string | null } | null;
}

export const api = {
  geoLookup: () => apiFetch<{ lat: number | null; lng: number | null; city: string | null; country: string | null }>("/cars/geolookup"),
  getStats: () => apiFetch<PlatformStats>("/cars/stats"),
  getFeaturedCars: (limit = 6, country?: string) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (country) params.set("country", country);
    return apiFetch<{ cars: CarCardData[] }>(`/cars/featured?${params}`);
  },
  // Task #6830 — batch validation of the hero's recently-viewed chips. Returns
  // fresh display fields for the ids that are still live; ids missing from the
  // response are delisted/removed and get pruned client-side.
  getCarChips: (ids: number[]) =>
    apiFetch<{ cars: Array<{
      id: number;
      slug: string;
      make: string;
      model: string;
      year: number | null;
      city: string | null;
      country_code: string | null;
      image: string | null;
    }> }>(`/cars/chips?ids=${ids.join(",")}`),
  getCarTypes: () => apiFetch<{ types: CarType[] }>("/cars/types"),
  getMakeCounts: () => apiFetch<{ makeCounts: MakeInventoryCount[] }>("/cars/make-counts"),
  getDestinations: (limit = 8, countryCode?: string) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (countryCode) params.set("country_code", countryCode);
    return apiFetch<{ countryCode?: string | null; destinations: Array<{ city: string; carCount: number; image: string | null }> }>(`/cars/destinations?${params}`);
  },
  getMakes: () => apiFetch<{ makes: string[] }>("/cars/makes"),
  getClassifications: () => apiFetch<{ premier: string[]; exotic: string[] }>("/cars/classifications"),
  searchCars: (params: SearchParams) => apiFetch<SearchResult>(searchRequestApiPath(params)),
  getBlockedCarIds: (dateFrom: string, dateTo: string, carIds: number[]) => {
    const sp = new URLSearchParams();
    sp.set("date_from", dateFrom);
    sp.set("date_to", dateTo);
    carIds.forEach(id => sp.append("car_id", String(id)));
    return apiFetch<{ blockedCarIds: number[] }>(`/cars/blocked-ids?${sp.toString()}`);
  },
  getCarDetail: async (
    id: number | string,
    slug: string,
    opts?: {
      preview?: boolean;
      previewAccessToken?: string;
      dateFrom?: string;
      dateTo?: string;
      rentalType?: "driving" | "chauffeured" | "event";
      idOnly?: boolean;
      publicArchive?: string;
      completeRelated?: boolean;
    },
  ) => {
    const detailParams = new URLSearchParams();
    // Full public review text must survive refresh as well as initial SSR.
    detailParams.set("all_reviews", "1");
    if (opts?.preview) detailParams.set("preview", "1");
    if (opts?.dateFrom && opts?.dateTo) {
      detailParams.set("date_from", opts.dateFrom);
      detailParams.set("date_to", opts.dateTo);
    }
    if (opts?.rentalType) detailParams.set("rental_type", opts.rentalType);
    if (opts?.publicArchive) {
      detailParams.set("public_archive", opts.publicArchive);
      if (typeof window !== "undefined" &&
          !["driveshare.com", "www.driveshare.com", "bookaclassic.us", "www.bookaclassic.us", "events.driveshare.com"].includes(window.location.hostname)) {
        detailParams.set("allow_delisted", "1");
      }
    }
    const qs = detailParams.size > 0 ? `?${detailParams.toString()}` : "";
    const headers: Record<string, string> = { "Content-Type": "application/json", "Accept": "application/json" };
    if (opts?.preview && opts.previewAccessToken) {
      headers["Authorization"] = `Bearer ${opts.previewAccessToken}`;
    }
    const detailPath = opts?.idOnly ? `/cars/${id}` : `/cars/${id}/${slug}`;
    const res = await fetch(`${API_BASE}${detailPath}${qs}`, { headers });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      // Attach the HTTP status so the page can distinguish an authoritative
      // 404 (listing confirmed gone → terminal "Car not found") from a
      // transient failure (5xx → retry state, noindex). Task #6672.
      const err = new Error(body.error || `API error ${res.status}`) as Error & { status: number };
      err.status = res.status;
      throw err;
    }
    const body = await res.json();
    if (body._slugRedirect) {
      const err = new Error("redirect") as Error & { redirect: string };
      err.redirect = body._slugRedirect;
      throw err;
    }
    if (opts?.completeRelated && !opts.preview && !opts.publicArchive) {
      try {
        const related = await apiFetch<{ cars: CarCardData[] }>(
          `/cars/${body.car.id}/${encodeURIComponent(body.car.slug)}/similar${qs}`,
        );
        if (!Array.isArray(related.cars)) throw new Error("Incomplete related car source");
        body.similarCars = related.cars;
      } catch (cause) {
        const err = new Error("Related car source unavailable", { cause }) as Error & { status: number };
        err.status = 503; // Never misclassify an existing car as a car-level 404.
        throw err;
      }
    }
    return body as CarDetail;
  },
  resolveLegacyUsCarUrl: (legacySlug: string, publicPath?: string, allowDelisted = false) =>
    apiFetch<{
      carId: number | string;
      currentSlug: string;
      canonicalPath?: string;
      legacySlug: string;
      countryCode: string;
      path: string;
      provenance: string;
      publicArchivePath?: string;
    }>(`/driveshare-car-urls/${encodeURIComponent(legacySlug)}${publicPath ? `?public_path=${encodeURIComponent(publicPath)}` : ""}${allowDelisted ? `${publicPath ? "&" : "?"}allow_delisted=1` : ""}`),
  getCarReviews: (id: number | string, slug: string, page = 1) => apiFetch<{ reviews: ReviewData[]; total: number }>(`/cars/${id}/${slug}/reviews?page=${page}`),
  // Task #2880 — `excludeTripId` lets the request-change flow drop the
  // caller's own booking from `blockedDates` so its original days remain
  // selectable in the calendar. The server only honours the exclusion
  // when the caller is authenticated via `token` AND is the renter or
  // host on that trip; otherwise the parameter is silently ignored.
  getCarAvailability: (
    id: number | string,
    slug: string,
    opts?: { excludeTripId?: number | string | null; token?: string | null },
  ) => {
    const qs = opts?.excludeTripId != null ? `?excludeTripId=${encodeURIComponent(String(opts.excludeTripId))}` : "";
    const init: RequestInit | undefined = opts?.token ? { headers: { Authorization: `Bearer ${opts.token}` } } : undefined;
    return apiFetch<AvailabilityData>(`/cars/${id}/${slug}/availability${qs}`, init);
  },
  getInsurancePackages: () => apiFetch<{ packages: InsurancePackage[] }>("/insurance-packages"),
  subscribeNewsletter: (email: string) =>
    apiFetch<{ success: boolean; duplicate?: boolean }>("/newsletter/subscribe", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  unsubscribeNewsletter: (email: string) =>
    // Existing email-footer page promises both newsletter and marketing opt-out.
    // Keep it on the global route, separate from newsletter-only removal.
    apiFetch<{ success: boolean }>("/email/unsubscribe", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
};

// Task #7947 — mirror of api-server/src/lib/countries.ts COUNTRY_CODE_TO_LOCALE.
// The client map previously covered only a subset, so cars hosted in the
// newer markets (PL, BR, …) canonicalised to bare /cars/... while the sitemap
// and prerender advertised /pl/cars/... — keep the two maps in lockstep.
const COUNTRY_CODE_TO_LOCALE: Record<string, string> = {
  GB: "uk", IE: "ie", DK: "dk", SE: "se", NZ: "nz", AU: "au",
  PT: "pt", IT: "it", CA: "ca", DE: "de", FR: "fr", ZA: "za",
  MT: "mt", ES: "es", US: "us", BR: "br", PL: "pl", SK: "sk",
  NO: "no", BG: "bg", CO: "co", FI: "fi", NL: "nl", BE: "be",
  EC: "ec", MX: "mx",
};

export function countryCodeToLocale(code: string): string {
  return countryCodeToMarketLocale(code)?.market || COUNTRY_CODE_TO_LOCALE[code.trim().toUpperCase()] || "";
}

const MILES_COUNTRIES = new Set(["US", "GB", "MM", "LR"]);

export function isMetricCountry(countryCode: string | null | undefined): boolean {
  if (!countryCode) return false;
  return !MILES_COUNTRIES.has(countryCode.trim().toUpperCase());
}

export function getDistanceUnit(countryCode: string | null | undefined): "mile" | "km" {
  return listingDistanceUnit(countryCode) === "mi" ? "mile" : "km";
}

export function getDistanceUnitPlural(countryCode: string | null | undefined): string {
  const unit = getDistanceUnit(countryCode);
  return unit === "mile" ? "miles" : "km";
}

export function isDsPlatformOrigin(value: unknown): boolean {
  return ["ds", "driveshare"].includes(String(value || "").trim().toLowerCase());
}

export function buildCarPath(car: {
  id: number | string;
  slug: string;
  countryCode?: string | null;
  country_code?: string | null;
  legacySlug?: string | null;
  legacy_slug?: string | null;
  platformOrigin?: string | null;
  platform_origin?: string | null;
  sourceId?: string | number | null;
  source_id?: string | number | null;
}): string {
  if (!Number.isSafeInteger(Number(car.id)) || Number(car.id) <= 0) return "";
  const cc = (car.countryCode || car.country_code || "").trim().toUpperCase();
  const identity = readResolvedCarIdentity(car, { market: cc ? countryCodeToLocale(cc) : "",
    legacySeoEnabled:isDriveshareLegacySeoEnabled() });
  // Missing projections are not inventory failures. Never guess a destination.
  if (identity.kind !== "resolved") return "";
  return identity.path;
}

/**
 * Wouter-Link variant of `buildCarPath`. Wouter prepends the active router
 * base (e.g. `/uk` when the renter is on a `/uk/...` page) to every Link
 * `href` — but `buildCarPath()` already includes that locale prefix, so
 * passing the raw value into a `<Link>` produces `/uk/uk/cars/...` and 404s.
 *
 * The `~` sentinel is wouter's documented escape: it tells the Link to
 * treat the rest of the path as already-absolute and to skip the base
 * concatenation (see node_modules/wouter/src/index.js — `targetPath[0] === "~"`).
 *
 * `fallbackCountryCode` covers list-endpoint car payloads that don't include
 * `country_code` (TripListItem, FavouriteCar, PublicProfile.cars, …). Without
 * the fallback, `buildCarPath` would emit a country-less `/cars/...` path and
 * the `~` sentinel would strip the user out of their current `/uk` locale.
 * Pass the active market's ISO country (e.g. `marketToCountry(market)` from
 * `useLocale()`) so same-locale navigation is preserved when the API didn't
 * carry it down.
 *
 * Use this helper for any `<Link href=...>` car link. Keep `buildCarPath()`
 * for raw `<a href=...>`, `window.open()`, SEO canonical URLs, sitemap
 * entries, and JSON-LD — those need the full locale-prefixed path with no
 * sentinel.
 */
export function buildCarLinkHref(
  car: Parameters<typeof buildCarPath>[0],
  fallbackCountryCode?: string | null,
): string {
  const cc = car.countryCode || car.country_code || fallbackCountryCode || null;
  const path = buildCarPath({ ...car, country_code: cc });
  return path ? `~${path}` : "";
}
